import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { CanonicalDocument } from '@besbello/design-core';
import { api } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { Banner, Card, Spinner } from '../components/ui.js';
import { MockupPrenda } from '../editor/MockupPrenda.js';

/**
 * UN DISEÑO COMPARTIDO, de solo lectura.
 *
 * Casi ningún pedido de grupo lo decide una sola persona. Quien recibe este
 * enlace no es el cliente: es alguien a quien el cliente le está enseñando su
 * diseño para que dé el visto bueno.
 *
 * Por eso aquí no hay editor, ni precio, ni forma de pedir. Y por eso la
 * página dice en voz alta que la vista es orientativa: va a acabar en un grupo
 * de WhatsApp, y allí ya no hay nadie para explicarlo.
 */

interface Compartido {
  productName: string;
  colorName: string;
  zoneId: string;
  viewCode: string;
  document: CanonicalDocument | null;
  expiresAt: string;
}

export function CompartidoPage() {
  const { token = '' } = useParams();
  const t = useT();
  const [datos, setDatos] = useState<Compartido | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api
      .verCompartido(token)
      .then((r) => {
        if (vivo) setDatos(r as Compartido);
      })
      .catch((err: Error) => {
        if (vivo) setError(err.message);
      });
    return () => {
      vivo = false;
    };
  }, [token]);

  if (error) {
    return (
      <div className="mx-auto max-w-lg p-4">
        <Banner tone="warning" title={t('shared.gone')}>{error}</Banner>
      </div>
    );
  }
  if (!datos) return <Spinner label={t('app.loading')} />;

  const documento = datos.document;

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <h1 className="text-xl font-semibold">{datos.productName}</h1>
      <p className="text-sm text-tinta-suave">{datos.colorName}</p>

      <Card className="mt-4">
        {documento ? (
          /*
            Se pinta con el MISMO componente que el editor, en modo mirar.

            Un visor aparte se separaría del editor en cuanto uno de los dos
            cambiara, y entonces el cliente aprobaría una cosa distinta de la
            que está diseñando.
          */
          <MockupPrenda
            document={documento}
            zonePolygonMm={[
              { x: 0, y: 0 },
              { x: documento.canvasMm.width, y: 0 },
              { x: documento.canvasMm.width, y: documento.canvasMm.height },
              { x: 0, y: documento.canvasMm.height },
            ]}
            encaje={{ imageUrl: null, mmPerPixel: 1.74, anchor: null, provenance: 'inherited', confidence: 0 }}
            assetUrls={{}}
            dark={false}
          />
        ) : (
          <p className="text-sm text-tinta-suave">{t('shared.noDesign')}</p>
        )}
      </Card>

      <p className="mt-3 text-xs text-tinta-suave">{t('shared.readOnly')}</p>
      <p className="mt-1 text-xs text-tinta-suave">
        {t('shared.expires', { fecha: new Date(datos.expiresAt).toLocaleDateString() })}
      </p>
    </div>
  );
}
